import { createContext, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/root";
import { fetchActivePlan } from "../../redux/team";
import { Network, NETWORK_INFO, SubscriptionTier } from "@util/constants.ts";

interface GasPoliciesQueryContextData {
  loading: boolean;
  supportedNetworks: Network[];
  teamTier: SubscriptionTier;
}

type GasPoliciesContextData = Omit<GasPoliciesQueryContextData, "loading">;

export function gasPoliciesFlagsFromRootState(
  state: RootState,
): GasPoliciesQueryContextData {
  const activePlan = state.team.activePlan;
  const teamTier = activePlan.value?.tier ?? SubscriptionTier.FREE;

  const supportedNetworks =
    activePlan.value?.tier === SubscriptionTier.FREE
      ? Object.values(NETWORK_INFO)
          .filter((n) => n.supportsGasPolicies && n.isTestNet)
          .map((n) => n.network)
      : Object.values(NETWORK_INFO)
          .filter((n) => n.supportsGasPolicies)
          .map((n) => n.network);

  return {
    supportedNetworks,
    loading: activePlan.isLoading,
    teamTier,
  };
}

export function useGasPoliciesFlags() {
  const dispatch = useDispatch();
  const flags = useSelector(gasPoliciesFlagsFromRootState);
  useEffect(() => {
    dispatch(fetchActivePlan());
  }, [dispatch]);
  return flags;
}

export const GasPoliciesContext = createContext<GasPoliciesContextData>({
  supportedNetworks: [],
  teamTier: SubscriptionTier.FREE,
});

/**
 * In order to use this, make sure your component is wrapped in GasManagerPageWrapper
 */
export function useGasPoliciesContext(): GasPoliciesContextData {
  const data = useContext(GasPoliciesContext);
  if (data === undefined) {
    throw new Error(
      "Gas Policies wasn't initialized, make sure you wrap the component inside <GasManagerPageWrapper />",
    );
  }
  return data;
}
